/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/CharacterizationFactors/AdminCharacterizationFactors.tsx.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../../../components/common/Button";
import OutlineButton from "../../../../components/common/OutlineButton";
import {
  characterizationFactorsAdminService,
  type CharacterizationFactorAdmin,
} from "../../../../services/characterizationFactorsAdminService";
import { useFuelMixDropdownOptions } from "../shared/useFuelMixDropdownOptions";
import "../Admin.css";
import "./AdminCharacterizationFactors.css";

const emptyForm = () => ({
  fuelMix: "",
  unit: "",
  characterizationFactor: "",
  sortOrder: "0",
  isActive: true,
});

const AdminCharacterizationFactors: React.FC = () => {
  const [rows, setRows] = useState<CharacterizationFactorAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const [deleteTarget, setDeleteTarget] = useState<CharacterizationFactorAdmin | null>(null);

  const { options: fuelMixOptions } = useFuelMixDropdownOptions();

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await characterizationFactorsAdminService.list();
      setRows(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load characterization factors");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const o = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        return o !== 0 ? o : a.id - b.id;
      }),
    [rows]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (r: CharacterizationFactorAdmin) => {
    setEditingId(r.id);
    setForm({
      fuelMix: r.fuelMix ?? "",
      unit: r.unit ?? "",
      characterizationFactor: r.characterizationFactor ?? "",
      sortOrder: String(r.sortOrder ?? 0),
      isActive: Boolean(r.isActive),
    });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleSave = async () => {
    const fuelMix = form.fuelMix.trim();
    const unit = form.unit.trim();
    const characterizationFactor = form.characterizationFactor.trim();
    if (!fuelMix || !unit || !characterizationFactor) {
      setError("Fuel mix, unit, and characterization factor are required.");
      return;
    }

    const sortOrder = Number.parseInt(String(form.sortOrder).trim(), 10);
    const sortOrderNum = Number.isFinite(sortOrder) ? sortOrder : 0;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingId == null) {
        await characterizationFactorsAdminService.create({
          fuelMix,
          unit,
          characterizationFactor,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        });
        setSuccess("Characterization factor created.");
      } else {
        await characterizationFactorsAdminService.update(editingId, {
          fuelMix,
          unit,
          characterizationFactor,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        });
        setSuccess("Characterization factor updated.");
      }
      closeModal();
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await characterizationFactorsAdminService.delete(deleteTarget.id);
      setSuccess("Deleted.");
      setDeleteTarget(null);
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="background-form-container analysis-shell admin-char-factors">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Characterization factors</span>
      </div>

      <div className="admin-form-root">
        <div className="dataset-container">
          <div className="dataset-section">
            <h3 className="dataset-section-title">Manage characterization factors</h3>
            <p className="muted" style={{ marginBottom: "1rem" }}>
              Values are used in LCA fuel-related calculations. Inactive rows are hidden from the
              public list (GET /characterization-factors). Admin list includes all rows.
            </p>

            {error && !modalOpen && (
              <div className="error-message" style={{ marginBottom: 12 }}>
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="success-message" style={{ marginBottom: 12 }}>
                <span className="success-icon">✅</span>
                <span>{success}</span>
              </div>
            )}

            <div className="char-factors-toolbar">
              <Button label="Add factor" type="button" onClick={openCreate} disabled={loading} />
            </div>

            {loading ? (
              <p>Loading…</p>
            ) : sortedRows.length === 0 ? (
              <p className="muted">No factors yet. Click &quot;Add factor&quot; to create one.</p>
            ) : (
              <div className="char-factors-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Fuel mix</th>
                      <th>Unit</th>
                      <th>Characterization factor</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th className="cell-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.sortOrder}</td>
                        <td>{r.fuelMix}</td>
                        <td>{r.unit}</td>
                        <td>{r.characterizationFactor}</td>
                        <td>
                          {r.isActive ? (
                            <span>Active</span>
                          ) : (
                            <span className="badge-inactive">Inactive</span>
                          )}
                        </td>
                        <td className="muted" style={{ fontSize: "0.8rem" }}>
                          {r.updatedAt
                            ? new Date(r.updatedAt).toLocaleString()
                            : "—"}
                        </td>
                        <td className="cell-actions">
                          <OutlineButton
                            label="Edit"
                            type="button"
                            onClick={() => openEdit(r)}
                          />
                          <OutlineButton
                            label="Delete"
                            type="button"
                            onClick={() => {
                              setError(null);
                              setDeleteTarget(r);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="modal-panel" role="dialog" aria-labelledby="char-factor-modal-title">
            <div className="modal-header" id="char-factor-modal-title">
              {editingId == null ? "New characterization factor" : "Edit characterization factor"}
            </div>
            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label htmlFor="cf-fuelMix">Fuel mix *</label>
                <select
                  id="cf-fuelMix"
                  value={
                    fuelMixOptions.some((o) => o.label === form.fuelMix)
                      ? form.fuelMix
                      : ""
                  }
                  onChange={(e) => setForm((f) => ({ ...f, fuelMix: e.target.value }))}
                  disabled={saving}
                >
                  <option value="">Select a fuel mix…</option>
                  {fuelMixOptions.map((o) => (
                    <option key={o.id} value={o.label}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {fuelMixOptions.length === 0 && (
                  <p className="muted">
                    No “Fuel Mix” dropdown options found. Add options in
                    “Manage dropdowns”.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="cf-unit">Unit *</label>
                <input
                  id="cf-unit"
                  type="text"
                  value={form.unit}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                  disabled={saving}
                  placeholder="e.g. kg CO₂eq/ kWh"
                />
              </div>
              <div>
                <label htmlFor="cf-factor">Characterization factor (display) *</label>
                <input
                  id="cf-factor"
                  type="text"
                  value={form.characterizationFactor}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, characterizationFactor: e.target.value }))
                  }
                  disabled={saving}
                  placeholder="e.g. 1.4 kg CO₂eq/ kWh (b)"
                />
              </div>
              <div>
                <label htmlFor="cf-sort">Sort order</label>
                <input
                  id="cf-sort"
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  disabled={saving}
                />
                <p className="muted">Lower numbers appear first in public lists.</p>
              </div>
              <div className="checkbox-row">
                <input
                  id="cf-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  disabled={saving}
                />
                <label htmlFor="cf-active">Active (visible on public API)</label>
              </div>
            </div>
            <div className="modal-footer">
              <OutlineButton label="Cancel" type="button" onClick={closeModal} disabled={saving} />
              <Button
                label={saving ? "Saving…" : "Save"}
                type="button"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setDeleteTarget(null);
          }}
        >
          <div className="modal-panel" role="alertdialog" aria-labelledby="cf-del-title">
            <div className="modal-header" id="cf-del-title">
              Delete factor?
            </div>
            <div className="modal-body">
              <p>
                Remove <strong>{deleteTarget.fuelMix}</strong> —{" "}
                {deleteTarget.characterizationFactor}?
              </p>
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <OutlineButton
                label="Cancel"
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={saving}
              />
              <Button label={saving ? "Deleting…" : "Delete"} type="button" onClick={confirmDelete} disabled={saving} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** React component — Admin Characterization Factors. */
export default AdminCharacterizationFactors;
